/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */

import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import { getAcronym, NUMBER_OF_ITEMS_PER_PAGE, VendorType } from '../../../../../Shared/Utilities/utils';
import theme from '../../../../../Shared/Themes/theme';
import { WorkOrderTeamData } from '../../../../Jobs/Components/AssociateOnTheJob/AssociateOnTheJob';
import {
  buttonStyles,
  CardContainerWrapper,
  DatePickerContainer,
  LogoWrapper,
} from '../../../../Jobs/Components/JobDetails/OneTimeJobDetails.Style';
import { useCallback, useEffect, useState } from 'react';
import DateSelection from '../../../../../Shared/Components/Common/DatePicker/DateSelection';
import { useForm, useWatch } from 'react-hook-form';
import { StyledPagination } from '../../../../../Shared/Components/Common/CommonStyle/Pagination.Style';
import { SearchFieldContainer, SearchInput } from '../../../../../Shared/Components/Layout/styles';
import { Icons } from '../../../../../Shared/Constants/Icons';
import { isEmpty } from 'lodash';
import { ManageAssignVendorInput } from '../AllJobs';
import {
  LogoWrapperDiv,
  PaginationDiv,
  RadioButtonStyles,
  ReassignOuterDiv,
  VendorCard,
  VendorText,
} from './ReassingVendor.Style';
import { velocitiOrganizationId, ZoomLevelClasses } from '../../../../../Shared/Constants/App';

interface ReassignVendorInterface {
  teamMembers: any[];
  setTotalCount?: (data: number) => void;
  setCurrentPage?: (data: number) => void;
  totalCount?: number;
  currentPage?: number;
  isDataLoading: boolean;
  setSelectedVendorData?: (data: ManageAssignVendorInput[]) => void;
  handleVendorConfirmButton?: () => void;
  handleCloseReassignVendorPopup?: () => void;
  fetchVendorsData?: (pageNumber?: number, limit?: number, searchString?: string) => void;
  reassignVendorJobData?: ManageAssignVendorInput[];
  selectedVendor?: string;
  isReassignVendorLoading?: boolean;
  selectedRows?: string[];
  pickerMode?: boolean;
}

export const checkAlreadySelected = (data: WorkOrderTeamData[], memberId: string): boolean => {
  return data.some((prevMember) => prevMember.id === memberId);
};

const ReassignVendor = ({
  teamMembers,
  isDataLoading,
  setCurrentPage,
  totalCount,
  currentPage,
  setSelectedVendorData,
  handleVendorConfirmButton,
  handleCloseReassignVendorPopup,
  fetchVendorsData,
  isReassignVendorLoading,
  selectedVendor,
  selectedRows,
  pickerMode,
}: ReassignVendorInterface) => {
  const { t } = useTranslation(['assignJob', 'oneTimeJob', 'calendar']);

  const [selectedMember, setSelectedMember] = useState<any>();
  const [currentZoomLevel, setCurrentZoomLevel] = useState<number>();
  const { control } = useForm({
    defaultValues: {
      specificDate: new Date().toISOString(),
    },
  });

  const mediumZoomLevel = 2;
  const normalZoomLevel = 1.25;

  const { radioStyles, formStyles } = RadioButtonStyles;
  const specificDate = useWatch({
    control,
    name: 'specificDate',
  });

  const { cancelButton, createJobButton, buttonText } = buttonStyles;

  const handleSelect = useCallback(
    (id: string) => {
      setSelectedMember(id);
    },
    [setSelectedMember]
  );
  const handleSelectedVendor = useCallback(
    (member: any) => {
      setSelectedVendorData?.([
        {
          vendorId: member?.vendorId,
          vendorName: member?.vendorCompanyName,
          vendorEmail: member?.email,
          fromDate: specificDate,
          mdVendorType: member?.vendorId === velocitiOrganizationId ? VendorType.SELF : VendorType.SUBCONTRACT,
        },
      ]);
    },
    [specificDate]
  );

  const isDisabled = (member: any) => {
    return !((selectedRows ?? []).length > 1) && selectedVendor === member.vendorId;
  };

  const getClassnameForZoomLevel = () => {
    if (!currentZoomLevel) return '';
    if (currentZoomLevel > normalZoomLevel && currentZoomLevel < mediumZoomLevel)
      return ZoomLevelClasses.NormalZoomLevel;
    if (currentZoomLevel >= mediumZoomLevel) {
      return ZoomLevelClasses.MediumZoomLevel;
    }
    return '';
  };

  const renderTeamContent = (teamMembers: any[], selectedMember: string, setSelectedMember: (id: string) => void) => {
    return (
      <>
        {teamMembers?.length > 0 && (
          <RadioGroup value={selectedMember} onChange={(e) => handleSelect(e.target.value)}>
            <ReassignOuterDiv className={getClassnameForZoomLevel()}>
              {teamMembers.map((member: any) => (
                <VendorCard
                  sx={{
                    opacity: isDisabled(member) ? 0.5 : 1,
                    cursor: isDisabled(member) ? 'not-allowed' : 'pointer',
                  }}
                  key={member.vendorId}
                  onClick={() => {
                    if (!isDisabled(member)) {
                      setSelectedMember(member.vendorId);
                      handleSelectedVendor(member);
                    }
                  }}>
                  <LogoWrapperDiv>
                    <LogoWrapper>
                      <Box>{getAcronym(member.doingBusinessAs).toUpperCase()}</Box>
                    </LogoWrapper>
                  </LogoWrapperDiv>
                  <VendorText>{member.doingBusinessAs}</VendorText>

                  <Box>
                    <FormControlLabel
                      value={member.vendorId}
                      control={
                        <Radio
                          checked={selectedMember === member.vendorId || isDisabled(member)}
                          disabled={isDisabled(member)}
                          sx={{
                            ...radioStyles,
                          }}
                        />
                      }
                      label=""
                      sx={{
                        ...formStyles,
                      }}
                    />
                  </Box>
                </VendorCard>
              ))}
            </ReassignOuterDiv>
          </RadioGroup>
        )}
      </>
    );
  };

  const renderVendorStartDate = () => {
    return (
      <DatePickerContainer
        sx={{
          marginBottom: '1rem',
          width: '95%',
        }}
        isDatePickerForTabletScreen={true}>
        <DateSelection
          requiredText={t('calendar:dateFieldIsRequired')}
          control={control}
          fieldName={'specificDate'}
          label={t('jobManagement:vendorStartDate')}
          isExcludePastDate={true}
          isExcludeFutureDate={false}
          textFieldStyles={{
            '& .MuiOutlinedInput-root': {
              '& fieldset,&:hover fieldset,&.Mui-focused fieldset': {
                borderWidth: 1,
                borderColor: theme.palette.primary.dark,
                borderRadius: '1rem',
              },
            },
            '& .MuiInputLabel-root': {
              color: theme.palette.primary.dark,
              '&.Mui-focused': {
                color: theme.palette.primary.dark,
              },
            },
          }}
        />
      </DatePickerContainer>
    );
  };

  const handleChange = (event: any, value: number): void => {
    setCurrentPage?.(value);
  };

  const handlePaginationChange = (event: any, value: number, searchString?: string) => {
    handleChange(event, value);
    fetchVendorsData?.(value, NUMBER_OF_ITEMS_PER_PAGE, searchString || '');
  };

  const renderConfirmButton = () => (
    <Box style={{ display: 'flex', justifyContent: 'center' }}>
      <Button
        disabled={isDataLoading || isEmpty(selectedMember) || isReassignVendorLoading}
        sx={{
          ...cancelButton,
          cursor: isDataLoading || isEmpty(selectedMember) || isReassignVendorLoading ? 'not-allowed' : 'pointer',
          maxWidth: '350px',
          marginTop: '2em',
          width: '100%',
          opacity: isDataLoading || isEmpty(selectedMember) || isReassignVendorLoading ? '0.5' : '1',
        }}
        onClick={handleCloseReassignVendorPopup}>
        <Typography sx={{ ...buttonText, color: theme.palette.primary.dark }}>{t('assignJob:cancel')}</Typography>
      </Button>
      <Button
        disabled={isDataLoading || isEmpty(selectedMember) || isReassignVendorLoading}
        sx={{
          ...createJobButton,
          cursor: isDataLoading || isEmpty(selectedMember) || isReassignVendorLoading ? 'not-allowed' : 'pointer',
          maxWidth: '350px',
          marginTop: '2em',
          width: '100%',
          opacity: isDataLoading || isEmpty(selectedMember) || isReassignVendorLoading ? '0.5' : '1',
        }}
        onClick={() => {
          if (pickerMode) {
            handleCloseReassignVendorPopup?.();
          } else {
            handleVendorConfirmButton?.();
          }
        }}>
        {isReassignVendorLoading ? (
          <CircularProgress size={30} sx={{ color: theme.palette.common.white }} />
        ) : (
          <Typography sx={{ ...buttonText, color: theme.palette.common.white }}>{t('assignJob:confirm')}</Typography>
        )}
      </Button>
    </Box>
  );

  useEffect(() => {
    const checkZoomLevel = () => {
      const zoomLevel = window.innerWidth / window.screen.width;
      setCurrentZoomLevel(zoomLevel);
    };
    checkZoomLevel();
    window.addEventListener('resize', checkZoomLevel);
    return () => window.removeEventListener('resize', checkZoomLevel);
  }, []);

  return (
    <>
      <CardContainerWrapper sx={{ height: '60vh', overflowX: 'hidden' }}>
        <SearchFieldContainer
          sx={{
            boxShadow: '0px 10px 20px 6px rgba(121, 87, 0, 0.10)',
            marginBottom: '2em',
            marginRight: '30px',
            width: '95%',
          }}>
          <SearchInput
            type="text"
            placeholder={t('jobManagement:searchVendor')}
            onChange={(event) => {
              handlePaginationChange({}, 1, event?.target?.value?.trim() ?? '');
            }}
          />
          <img src={Icons.SearchIcon} alt={t('altTexts:search')} style={{ width: '1.5rem', height: '1.5rem' }} />
        </SearchFieldContainer>
        {renderVendorStartDate()}
        {isDataLoading ? (
          <CircularProgress size={30} sx={{ color: theme.palette.primary.dark, marginLeft: '45%', marginTop: '10%' }} />
        ) : teamMembers?.length > 0 ? (
          renderTeamContent(teamMembers, selectedMember, setSelectedMember)
        ) : (
          <Box sx={{ marginTop: '10%' }}>
            <Typography sx={{ display: 'flex', justifyContent: 'center' }}>{t('assignJob:noResultsFound')}</Typography>
          </Box>
        )}
      </CardContainerWrapper>

      {!isDataLoading && totalCount && totalCount > 10 && (
        <PaginationDiv>
          <Stack spacing={2} justifyContent="center" style={{ marginTop: '20px' }}>
            <StyledPagination
              count={Math.ceil(totalCount / NUMBER_OF_ITEMS_PER_PAGE)}
              page={currentPage}
              onChange={handlePaginationChange}
              color="primary"
            />
          </Stack>
        </PaginationDiv>
      )}
      {!isDataLoading && renderConfirmButton()}
    </>
  );
};

export default ReassignVendor;
